import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import QRCode from 'react-qr-code';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import * as XLSX from 'xlsx';
import { getAll } from '../api';
import {
  getStockItemsById,
  createStockItems,
  updateStockItems,
  deleteStockItems,
} from '../api/stock-itemsAPI';
import { getStockItemses } from '../api/stock-itemsAPI';
import { getStockItemStatus } from '../api/stock-item-statusAPI';
import { getParts, updateParts } from '../api/partsAPI';
import { createStockItemsQuantities, updateStockItemsQuantities } from '../api/stock-items-quantitiesAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useValidator } from '../utils/useValidator';
import StockItemsDataTable from './StockItemsDataTable';
import { useTableFilters } from '../utils/useTableFilters';

const PARTS_PAGE_SIZE = 10;

const StockItemsLayer = () => {
  const userProfile = useUserProfile();
  const [stockItems, setStockItems] = useState([]);
  const [newItem, setNewItem] = useState({
    currencyId: 1,
    measurementUnitId: 5,
    quantity: 1,
    statusId: 1,
    partId: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedPartOption, setSelectedPartOption] = useState(null);
  const [stockItemStatuses, setStockItemStatuses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [quantityRows, setQuantityRows] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [warehouseLocations, setWarehouseLocations] = useState([]);

  const tableFilters = useMemo(
    () => [
      { field: 'locationId', type: 'dropDown', label: 'Location', api: 'locations', displayField: 'name', menuBarFilter: 1 },
      { field: 'serialised', type: 'switch', label: 'Serialised Part', menuBarFilter: 1 },
      { field: 'outOfStock', type: 'switch', label: 'Out of Stock', menuBarFilter: 1 },
    ],
    [],
  );

  const {
    appliedFilters,
    renderFilterControls,
    tableKey,
    setTableKeyLocal,
  } = useTableFilters(tableFilters);

  const fetchStockItems = useCallback(async () => {
    try {
      const data = await getStockItemses({
        sortField: sortField || undefined,
        sortOrder: sortOrder || undefined,
      });
      setStockItems(data);
    } catch (err) {
      console.error('Error fetching stock items:', err);
    }
  }, [sortField, sortOrder]);

  useEffect(() => {
    fetchStockItems();
  }, [fetchStockItems]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const result = await getStockItemStatus();
        setStockItemStatuses(result?.data || result || []);
      } catch (err) {
        console.error('Error fetching stock item statuses:', err);
      }
    };

    fetchStatuses();
  }, []);

  const filteredStockItemStatuses = useMemo(() => {
    const isNonSerialised = Number(newItem?.serialised ?? 0) === 0;
    if (!isNonSerialised) {
      return stockItemStatuses;
    }
    return stockItemStatuses.filter(
      (status) => Number(status?.serialisedOnly ?? 0) === 0,
    );
  }, [stockItemStatuses, newItem?.serialised]);

  useEffect(() => {
    let isMounted = true;
    const fetchLocations = async () => {
      try {
        const result = await getAll('locations');
        const locations = Array.isArray(result) ? result : result?.data || [];
        if (isMounted) {
          setWarehouseLocations(locations);
        }
      } catch (err) {
        console.error('Error fetching warehouse locations:', err);
      }
    };

    fetchLocations();

    return () => {
      isMounted = false;
    };
  }, []);

  const defaultWarehouseId = useMemo(() => {
    if (warehouseLocations.length !== 1) return null;
    return warehouseLocations[0]?.id ?? null;
  }, [warehouseLocations]);

  useEffect(() => {
    if (!defaultWarehouseId) return;
    setNewItem((prev) => {
      if (prev?.warehouseId) return prev;
      return {
        ...prev,
        warehouseId: defaultWarehouseId,
      };
    });
  }, [defaultWarehouseId]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const result = await getAll('locations');
        setLocations(result?.data || result || []);
      } catch (err) {
        console.error('Error fetching locations:', err);
      }
    };

    fetchLocations();
  }, []);

  const buildLocationCode = useCallback((aisle, bay, shelf, zone) => {
    const parts = [aisle, bay, shelf, zone]
      .map((value) => (typeof value === 'string' ? value.trim() : value))
      .filter((value) => Boolean(value));
    return parts.length ? parts.join('-') : null;
  }, []);

  const createEmptyQuantityRow = useCallback(
    ({ isNew = true } = {}) => ({
      id: null,
      warehouseId: '',
      stockItemStatusId: '',
      aisle: '',
      bay: '',
      shelf: '',
      quantity: '',
      isNew,
    }),
    [],
  );

  const formatWarehouseList = useCallback((quantities) => {
    if (!Array.isArray(quantities)) return '';
    const names = quantities
      .map((quantity) => quantity?.warehouse?.name)
      .filter(Boolean);
    return Array.from(new Set(names)).join(', ');
  }, []);

  const filteredItems = useMemo(() => {
    let items = stockItems;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter((item) =>
        item.parts?.some((part) =>
          part?.partNumber?.toLowerCase().includes(term) ||
          part?.description?.toLowerCase().includes(term),
        ) ||
        item.stockItemsQuantities?.some((quantity) =>
          quantity?.warehouse?.name?.toLowerCase()?.includes(term),
        )
      );
    }

    if (appliedFilters.locationId?.length) {
      items = items.filter((i) =>
        i.stockItemsQuantities?.some((quantity) =>
          appliedFilters.locationId.includes(quantity?.warehouseId),
        ),
      );
    }
    if (appliedFilters.serialised) {
      items = items.filter((i) => i.serialised);
    }
    if (appliedFilters.outOfStock) {
      items = items.filter((i) => i.stockOnHand === 0);
    }

    return items;
  }, [stockItems, searchTerm, appliedFilters]);

  const formatPartOptionLabel = useCallback((partNumber, description) => {
    const safeNumber = typeof partNumber === 'string' || typeof partNumber === 'number'
      ? String(partNumber).trim()
      : '';
    const safeDescription = typeof description === 'string' ? description.trim() : '';
    return [safeNumber, safeDescription].filter(Boolean).join(' – ');
  }, []);

  const getPrimaryPart = useCallback((parts) => {
    if (Array.isArray(parts)) {
      return parts[0] ?? null;
    }
    return parts ?? null;
  }, []);

  const getPartNumbersList = useCallback((parts) => {
    if (!Array.isArray(parts)) return '';
    return parts
      .map((part) => part?.partNumber)
      .filter(Boolean)
      .join(', ');
  }, []);

  const getPartDescriptionsList = useCallback((parts) => {
    if (!Array.isArray(parts)) return '';
    return parts
      .map((part) => part?.description)
      .filter(Boolean)
      .join(', ');
  }, []);

  const handlePartSearch = useCallback(
    async (inputValue = '', { page } = {}) => {
      const raw = typeof inputValue === 'string' ? inputValue : inputValue?.search ?? '';
      const trimmed = raw.trim();
      const pageNumber = typeof page === 'number' && page > 0 ? page : 1;
      const shouldLoadDefault = trimmed === '';

      if (!shouldLoadDefault && trimmed.length < 2) {
        return { options: [], hasMore: false };
      }

      try {
        const resp = await getParts({
          page: pageNumber,
          pageSize: PARTS_PAGE_SIZE,
          search: shouldLoadDefault ? '' : trimmed,
        });
        const items = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        const options = items.map((item) => ({
          value: item.id,
          label: formatPartOptionLabel(item.partNumber, item.description) || `Part ${item.id}`,
          raw: item,
        }));
        const totalCount =
          typeof resp?.total === 'number'
            ? resp.total
            : typeof resp?.count === 'number'
              ? resp.count
              : typeof resp?.meta?.total === 'number'
                ? resp.meta.total
                : undefined;
        const hasMore =
          typeof resp?.hasMore === 'boolean'
            ? resp.hasMore
            : typeof totalCount === 'number'
              ? pageNumber * PARTS_PAGE_SIZE < totalCount
              : items.length === PARTS_PAGE_SIZE;

        return {
          options,
          totalCount,
          pageSize: PARTS_PAGE_SIZE,
          hasMore,
        };
      } catch (err) {
        console.error('Error fetching parts:', err);
        return { options: [], hasMore: false };
      }
    },
    [formatPartOptionLabel],
  );

  const serialNumberField = useMemo(
    () => ({ field: 'serialNumber', label: 'Serial Number', type: 'text', size: 6, maxLength: 50 }),
    [],
  );

  const baseAddFields = useMemo(
    () => [
      { field: 'materialNumber', label: 'Stock Item Number', type: 'text', size: 6, required: true },
      {
        field: 'partId',
        label: 'Part Number',
        type: 'paginated-select',
        size: 6,
        required: true,
        loadOptions: handlePartSearch,
        selectedOption: selectedPartOption,
        setSelectedOption: setSelectedPartOption,
        isClearable: true,
        onChange: (option, setFormData, field, setSelectedOptionFn) => {
          if (setSelectedOptionFn) {
            setSelectedOptionFn(option ?? null);
          }
          setFormData((prev) => ({
            ...prev,
            [field]: option?.value ?? null,
          }));
        },
      },
      { field: 'description', label: 'Description', type: 'text', size: 10, required: true },
      { field: 'serialised', label: 'Serialised Part', type: 'switch', size: 2 },
      { field: 'unitCost', label: 'Unit Cost', type: 'number', size: 4, mustBeGreaterThanZero: true },
      { field: 'currencyId', label: 'Currency', type: 'select', size: 4, api: 'currency', displayField: 'symbol' },
      { field: 'measurementUnitId', label: 'Measurement Unit', type: 'select', size: 4, api: 'measurement-units', displayField: 'unitCode', required: true },
      { field: 'stockMin', label: 'Minimum Stock', type: 'number', size: 3 },
      { field: 'stockMax', label: 'Maximum Stock', type: 'number', size: 3 },
      { field: 'reorderPoint', label: 'Report Point', type: 'number', size: 3 },
      { field: 'cycleCountFreq', label: 'Stock Take Frequency (Days)', type: 'number', size: 3 },
    ],
    [handlePartSearch, selectedPartOption],
  );

  const baseEditFields = useMemo(
    () => [
      { field: 'materialNumber', label: 'Stock Item Number', type: 'text', size: 6 },
      {
        field: 'partId',
        label: 'Part Number',
        type: 'paginated-select',
        size: 6,
        loadOptions: handlePartSearch,
        selectedOption: selectedPartOption,
        setSelectedOption: setSelectedPartOption,
        isClearable: false,
        disabled: true,
      },
      { field: 'description', label: 'Description', type: 'text', size: 10, required: true },
      { field: 'serialised', label: 'Serialised Part', type: 'switch', size: 2 },
      { field: 'unitCost', label: 'Unit Cost', type: 'number', size: 4, mustBeGreaterThanZero: false },
      { field: 'currencyId', label: 'Currency', type: 'select', size: 4, api: 'currency', displayField: 'symbol' },
      { field: 'measurementUnitId', label: 'Measurement Unit', type: 'select', size: 4, api: 'measurement-units', displayField: 'unitCode', required: true },
      { field: 'stockMin', label: 'Minimum Stock', type: 'number', size: 3 },
      { field: 'stockMax', label: 'Maximum Stock', type: 'number', size: 3 },
      { field: 'reorderPoint', label: 'Report Point', type: 'number', size: 3 },
      { field: 'cycleCountFreq', label: 'Stock Take Frequency (Days)', type: 'number', size: 3 },
    ],
    [handlePartSearch, selectedPartOption],
  );

  const lastFields = useMemo(
    () => [
      { field: 'stockDescription', label: 'Notes', type: 'textarea', size: 12 },
      { field: 'stockImage', label: 'Stock Image', type: 'image', size: 6, directory: 'partsImages' },
    ],
    [],
  );

  const addFields = useMemo(() => {
    if (newItem?.serialised) {
      return [...baseAddFields, serialNumberField, ...lastFields];
    }
    return [...baseAddFields, ...lastFields];
  }, [baseAddFields, newItem?.serialised, serialNumberField, lastFields]);

  const editFields = useMemo(() => {
    if (newItem?.serialised) {
      return [...baseEditFields, serialNumberField, ...lastFields];
    }
    return [...baseEditFields, ...lastFields];
  }, [baseEditFields, newItem?.serialised, serialNumberField, lastFields]);

  const activeFields = useMemo(
    () => (editingId ? editFields : addFields),
    [editingId, editFields, addFields],
  );

  const { errors, setErrors, validate } = useValidator(activeFields, newItem);

  const { renderField: renderAddField } = useFormBuilder(addFields, newItem, setNewItem, errors);
  const { renderField: renderEditField } = useFormBuilder(editFields, newItem, setNewItem, errors);

  const resetForm = useCallback(() => {
    setEditingId(null);
    setNewItem({
      currencyId: 1,
      measurementUnitId: 5,
      quantity: 1,
      statusId: 1,
      warehouseId: defaultWarehouseId ?? null,
      partId: null,
    });
    setSelectedPartOption(null);
    setQuantityRows([createEmptyQuantityRow({ isNew: false })]);
    setErrors({});
  }, [createEmptyQuantityRow, defaultWarehouseId, setErrors]);

  useEffect(() => {
    if (!showModal) {
      setQuantityRows([createEmptyQuantityRow({ isNew: false })]);
      setErrors({});
    }
  }, [showModal, createEmptyQuantityRow, setErrors]);

  const qrValue = useMemo(() => {
    if (!editingId) return '';
    const primaryPart = getPrimaryPart(newItem.parts);
    const data = {
      partNumber: primaryPart?.partNumber,
      oemNumber: primaryPart?.oemNumber,
      weight: primaryPart?.weight,
      id: newItem.id,
      description: primaryPart?.description,
    };
    return JSON.stringify(data);
  }, [editingId, newItem, getPrimaryPart]);

  const handleEdit = async (item) => {
    try {
      const data = await getStockItemsById(item.id);
      const grouped = new Map();
      (data.stockItemsQuantities || []).forEach((quantity) => {
        const key = [
          quantity.stockItemId,
          quantity.warehouseId ?? 'null',
          quantity.locationCode ?? 'null',
          quantity.stockItemStatusId ?? 'null',
        ].join('|');

        const existing = grouped.get(key);
        const quantityValue = Number(quantity.quantity ?? 0);

        if (existing) {
          existing.quantity = Number(existing.quantity) + quantityValue;
        } else {
          grouped.set(key, {
            id: quantity.id,
            warehouseId: quantity.warehouseId ?? '',
            stockItemStatusId: quantity.stockItemStatusId ?? '',
            aisle: quantity.aisle ?? '',
            bay: quantity.bay ?? '',
            shelf: quantity.shelf ?? '',
            quantity: quantityValue,
          });
        }
      });

      const rows = Array.from(grouped.values()).map((row) => ({
        ...row,
        quantity: row.quantity === 0 ? 0 : row.quantity || '',
        isNew: false,
      }));
      const primaryPart = getPrimaryPart(data.parts);
      setNewItem({
        ...data,
        partId: primaryPart?.id ?? null,
      });
      if (primaryPart?.id) {
        setSelectedPartOption({
          value: primaryPart.id,
          label: formatPartOptionLabel(primaryPart.partNumber, primaryPart.description) || `Part ${primaryPart.id}`,
          raw: primaryPart,
        });
      } else {
        setSelectedPartOption(null);
      }
      setQuantityRows(rows.length ? rows : [createEmptyQuantityRow()]);
      setEditingId(item.id);
      setShowModal(true);
    } catch (err) {
      console.error('Error loading stock item:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this stock item?')) return;
    try {
      await deleteStockItems(id);
      await fetchStockItems();
    } catch (err) {
      console.error('Error deleting stock item:', err);
    }
  };

  const generateCsv = () => {
    const headers = [
      'Part Number',
      'Description',
      'Location',
      'Stock On Hand',
      'Available',
      'Reserved',
      'On Back Order',
      'Unit Cost',
      'Unit',
    ];

    const rows = filteredItems.map((row) => [
      getPartNumbersList(row.parts),
      getPartDescriptionsList(row.parts),
      formatWarehouseList(row.stockItemsQuantities),
      row.stockOnHand ?? '',
      row.available ?? '',
      row.reserved ?? '',
      row.backOrder ?? '',
      row.unitCost ?? '',
      row.measurementUnit?.unitCode || '',
    ]);

    const csvLines = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = [
      'Part Number',
      'Description',
      'Location',
      'Stock On Hand',
      'Available',
      'Reserved',
      'On Back Order',
      'Unit Cost',
      'Unit',
    ];

    const rows = filteredItems.map((row) => [
      getPartNumbersList(row.parts),
      getPartDescriptionsList(row.parts),
      formatWarehouseList(row.stockItemsQuantities),
      row.stockOnHand ?? '',
      row.available ?? '',
      row.reserved ?? '',
      row.backOrder ?? '',
      row.unitCost ?? '',
      row.measurementUnit?.unitCode || '',
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'StockItems');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'stockitems.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'stockitems.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    const isValid = validate();
    if (!isValid) return;
    try {
      const payload = {};
      for (const key in newItem) {
        if (key === 'partId') continue;
        if (newItem[key] !== null && typeof newItem[key] !== 'object') {
          payload[key] = newItem[key];
        }
      }

      const cleanedRows = quantityRows
        .map((row) => ({
          id: row.id ?? null,
          warehouseId: row.warehouseId ? Number(row.warehouseId) : null,
          stockItemStatusId: row.stockItemStatusId ? Number(row.stockItemStatusId) : null,
          aisle: row.aisle || null,
          bay: row.bay || null,
          shelf: row.shelf || null,
          quantity:
            row.quantity === '' || row.quantity === null || row.quantity === undefined
              ? null
              : Number(row.quantity),
        }))
        .filter((row) =>
          [
            row.warehouseId,
            row.stockItemStatusId,
            row.aisle,
            row.bay,
            row.shelf,
            row.quantity !== null ? row.quantity : null,
          ].some((value) => value !== null && value !== ''),
        );

      if (!editingId || cleanedRows.length) {
        payload.stockItemQuantities = cleanedRows.map((row) => ({
          id: row.id ?? null,
          warehouseId: row.warehouseId,
          stockItemStatusId: row.stockItemStatusId,
          aisle: row.aisle,
          bay: row.bay,
          shelf: row.shelf,
          quantity: row.quantity,
          locationCode: buildLocationCode(row.aisle, row.bay, row.shelf, null),
        }));
      }

      let savedStockItem;
      if (editingId) {
        savedStockItem = await updateStockItems(editingId, payload);
      } else {
        savedStockItem = await createStockItems(payload);
      }

      const selectedPartId = newItem.partId ?? selectedPartOption?.value ?? null;
      if (!editingId && selectedPartId && savedStockItem?.id) {
        await updateParts(selectedPartId, {
          stockItemId: savedStockItem.id,
          createStockItem: true,
        });
      }

      setShowModal(false);
      resetForm();
      await fetchStockItems();
    } catch (err) {
      console.error('Error saving stock item:', err);
    }
  };

  const handleSortChange = useCallback(
    (field) => {
      if (sortField === field) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else if (sortOrder === 'desc') {
          setSortField(null);
          setSortOrder(null);
        } else {
          setSortOrder('asc');
        }
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [sortField, sortOrder],
  );

  const quantityRowWarehouseCounts = useMemo(() => {
    if (!editingId) return new Map();
    const counts = new Map();
    quantityRows.forEach((row) => {
      const key = row.warehouseId ?? '';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return counts;
  }, [quantityRows, editingId]);

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div className="d-flex flex-wrap align-items-center gap-2 ms-3">
          {renderFilterControls()}
        </div>

        <div className="d-flex align-items-center gap-2 ms-auto me-8">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setTableKeyLocal();
            }}
            style={{ width: '200px' }}
          />
        </div>
        <div className="dropdown me-2">
          <button
            className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            Export Data
          </button>
          <ul className="dropdown-menu p-12 border bg-base shadow">
            <li>
              <button className="dropdown-item" onClick={handleExportExcel}>
                Excel
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={handleExportCSV}>
                CSV
              </button>
            </li>
          </ul>
        </div>
        {userProfile?.privileges?.includes('stock-items:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" /> Add Stock Item
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <StockItemsDataTable
            key={`table-${tableKey}`}
            items={filteredItems}
            onEdit={handleEdit}
            onDelete={handleDelete}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSortChange}
          />
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Stock Item' : 'New Stock Item'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {(editingId ? editFields : addFields)
                    .filter(
                      (field) =>
                        !['stockDescription', 'stockImage', 'serialNumber'].includes(field.field),
                    )
                    .map(editingId ? renderEditField : renderAddField)}
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="mb-0 text-lg">Warehouse Quantities</h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                        onClick={() =>
                          setQuantityRows((prev) => [...prev, createEmptyQuantityRow()])
                        }
                      >
                        <Icon icon="ic:baseline-plus" />
                        Add Stock Transaction
                      </button>
                    </div>
                  </div>
                  {quantityRows.map((row, index) => {
                    const key = row.warehouseId ?? '';
                    const countForWarehouse = quantityRowWarehouseCounts.get(key) ?? 0;
                    const normalizedQuantity =
                      row.quantity === '' || row.quantity === null || row.quantity === undefined
                        ? null
                        : Number(row.quantity);
                    const shouldHideRow =
                      editingId && normalizedQuantity === 0 && countForWarehouse > 1;
                    if (shouldHideRow) return null;
                    const showLabels = index === 0;
                    const canDeleteRow = !editingId || row.isNew;

                    return (
                      <div className="col-12" key={`quantity-row-${index}`}>
                        <div className="row g-3 align-items-end">
                          <div className="col-md-3">
                            {showLabels && <label className="form-label">Warehouse</label>}
                            <select
                              className="form-select"
                              value={row.warehouseId ?? ''}
                              onChange={(event) => {
                                const value = event.target.value;
                                setQuantityRows((prev) =>
                                  prev.map((item, idx) =>
                                    idx === index
                                      ? { ...item, warehouseId: value }
                                      : item,
                                  ),
                                );
                              }}
                            >
                              <option value="">Select...</option>
                              {locations.map((location) => (
                                <option key={location.id} value={location.id}>
                                  {location.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-3">
                            {showLabels && <label className="form-label">Status</label>}
                            <select
                              className="form-select"
                              value={row.stockItemStatusId ?? ''}
                              onChange={(event) => {
                                const value = event.target.value;
                                setQuantityRows((prev) =>
                                  prev.map((item, idx) =>
                                    idx === index
                                      ? { ...item, stockItemStatusId: value }
                                      : item,
                                  ),
                                );
                              }}
                            >
                              <option value="">Select...</option>
                              {filteredStockItemStatuses.map((status) => (
                                <option key={status.id} value={status.id}>
                                  {status.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-1">
                            {showLabels && <label className="form-label">Aisle</label>}
                            <input
                              type="text"
                              className="form-control"
                              value={row.aisle ?? ''}
                              onChange={(event) => {
                                const value = event.target.value;
                                setQuantityRows((prev) =>
                                  prev.map((item, idx) =>
                                    idx === index
                                      ? { ...item, aisle: value }
                                      : item,
                                  ),
                                );
                              }}
                            />
                          </div>
                          <div className="col-md-1">
                            {showLabels && <label className="form-label">Bay</label>}
                            <input
                              type="text"
                              className="form-control"
                              value={row.bay ?? ''}
                              onChange={(event) => {
                                const value = event.target.value;
                                setQuantityRows((prev) =>
                                  prev.map((item, idx) =>
                                    idx === index
                                      ? { ...item, bay: value }
                                      : item,
                                  ),
                                );
                              }}
                            />
                          </div>
                          <div className="col-md-1">
                            {showLabels && <label className="form-label">Shelf</label>}
                            <input
                              type="text"
                              className="form-control"
                              value={row.shelf ?? ''}
                              onChange={(event) => {
                                const value = event.target.value;
                                setQuantityRows((prev) =>
                                  prev.map((item, idx) =>
                                    idx === index
                                      ? { ...item, shelf: value }
                                      : item,
                                  ),
                                );
                              }}
                            />
                          </div>
                          <div className="col-md-2">
                            {showLabels && <label className="form-label">Quantity</label>}
                            <input
                              type="number"
                              className="form-control"
                              step={1}
                              min={0}
                              value={row.quantity ?? ''}
                              onChange={(event) => {
                                const value = event.target.value;
                                setQuantityRows((prev) =>
                                  prev.map((item, idx) =>
                                    idx === index
                                      ? { ...item, quantity: value }
                                      : item,
                                  ),
                                );
                              }}
                            />
                          </div>
                          {canDeleteRow && (
                            <div className="col-md-1 d-flex justify-content-end">
                              <button
                                type="button"
                                className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                onClick={() =>
                                  setQuantityRows((prev) =>
                                    prev.filter((_, idx) => idx !== index),
                                  )
                                }
                                disabled={!editingId && index === 0}
                              >
                                <Icon icon="ic:baseline-delete-outline" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(editingId ? editFields : addFields)
                    .filter((field) => field.field === 'serialNumber')
                    .map(editingId ? renderEditField : renderAddField)}
                  {(editingId ? editFields : addFields)
                    .filter((field) => field.field === 'stockDescription' || field.field === 'stockImage')
                    .map(editingId ? renderEditField : renderAddField)}
                  {/*{newItem.stockImage && (
                    <div className="col-md-6">
                      <div className="text-left">
                        <img
                          src={`${process.env.REACT_APP_SPACES_BASE}/${newItem.stockImage}`}
                          alt="User"
                          style={{
                            height: '200px',
                            objectFit: 'cover',
                            borderRadius: '6px',
                            border: '1px solid #ccc',
                          }}
                        />
                      </div>
                    </div>
                  )}*/}
                  <div className="col-md-6 d-flex align-items-end justify-content-end mb-2 pe-12">
                    <QRCode value={qrValue} size={128} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockItemsLayer;
